import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import type { Collateral, Company, Facility } from './case/types';
import {
  COLLATERAL_STATUSES,
  COLLATERAL_TYPES,
  fmtMoney,
  fmtRate,
  labelOf,
} from './case/types';

/**
 * CollateralsPage — standalone, deep-edit screen for collaterals.
 *
 * Routed at `/business/cases/:caseId/collaterals`. This is the screen the
 * advisor opens when they need to manage collaterals at scale or edit
 * fields beyond the quick-entry path: explicit haircut workflow, the full
 * facility-pledge picker, and (in the future) value snapshots and
 * release/reassignment.
 *
 * For the day-to-day "add a few rows during case review" flow, see the
 * CreditPortfolioTab inside CaseDetailPage. Both views read the same
 * `/collaterals` endpoint and share types/constants from `./case/types.ts`.
 */

// Page-local: short labels for the facility-pledge picker. The picker shows
// many facilities tightly stacked, so we use compact labels here rather than
// the verbose INSTRUMENT_TYPES copy used in form dropdowns.
const INSTRUMENT_LABEL_HE: Record<string, string> = {
  credit_line: 'חח״ד',
  short_term_loan: 'הל׳ קצרת מועד',
  long_term_loan: 'הל׳ ארוכת מועד',
  mortgage: 'משכנתא',
  bridge_loan: 'גישור',
  factoring: 'פקטורינג',
  leasing: 'ליסינג',
  government_guarantee: 'ערבות מדינה',
  off_balance_sheet: 'מחוץ למאזן',
};

const facilityLabel = (f: Facility) => {
  const parts = [
    f.facility_label,
    INSTRUMENT_LABEL_HE[f.instrument_type] ?? f.instrument_type,
    f.bank_name,
  ].filter(Boolean);
  return parts.join(' · ') || f.id.slice(0, 8);
};

type CollateralFormState = {
  company_id: string;
  collateral_type: string;
  description: string;
  status: string;
  value_current: string;
  haircut_percent: string;
  secures_facility_id: string;
  notes: string;
};

const emptyForm: CollateralFormState = {
  company_id: '',
  collateral_type: 'real_estate',
  description: '',
  status: 'existing',
  value_current: '',
  haircut_percent: '',
  secures_facility_id: '',
  notes: '',
};

export function CollateralsPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<CollateralFormState>(emptyForm);

  const companiesQuery = useQuery<Company[]>({
    queryKey: ['companies', caseId],
    queryFn: () => apiClient.get<Company[]>(`/companies?case_id=${caseId}`),
    enabled: !!caseId,
  });

  const facilitiesQuery = useQuery<Facility[]>({
    queryKey: ['facilities', 'case', caseId],
    queryFn: () => apiClient.get<Facility[]>(`/facilities?case_id=${caseId}`),
    enabled: !!caseId,
  });

  const collateralsQuery = useQuery<Collateral[]>({
    queryKey: ['collaterals', 'case', caseId],
    queryFn: () => apiClient.get<Collateral[]>(`/collaterals?case_id=${caseId}`),
    enabled: !!caseId,
  });

  const companyMap = useMemo(() => {
    const m = new Map<string, Company>();
    for (const c of companiesQuery.data ?? []) m.set(c.id, c);
    return m;
  }, [companiesQuery.data]);

  const facilityMap = useMemo(() => {
    const m = new Map<string, Facility>();
    for (const f of facilitiesQuery.data ?? []) m.set(f.id, f);
    return m;
  }, [facilitiesQuery.data]);

  // Picker shows only facilities that belong to the selected company
  const facilitiesForSelectedCompany = useMemo(() => {
    if (!form.company_id) return [];
    return (facilitiesQuery.data ?? []).filter(
      (f) => f.company_id === form.company_id
    );
  }, [form.company_id, facilitiesQuery.data]);

  const createCollateral = useMutation<Collateral, Error, CollateralFormState>({
    mutationFn: (state) => {
      const body: Record<string, unknown> = {
        company_id: state.company_id,
        collateral_type: state.collateral_type,
        status: state.status,
      };
      if (state.description) body.description = state.description;
      if (state.value_current) body.value_current = state.value_current;
      if (state.haircut_percent) {
        body.haircut_ratio = (Number(state.haircut_percent) / 100).toString();
      }
      if (state.secures_facility_id) {
        body.secures_facility_id = state.secures_facility_id;
      }
      if (state.notes) body.notes = state.notes;
      return apiClient.post<Collateral>('/collaterals', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['collaterals', 'case', caseId],
      });
      setForm(emptyForm);
      setIsAdding(false);
    },
  });

  const deleteCollateral = useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/collaterals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['collaterals', 'case', caseId],
      });
    },
  });

  const openAdd = () => {
    const firstCompany = companiesQuery.data?.[0];
    setForm({ ...emptyForm, company_id: firstCompany?.id ?? '' });
    setIsAdding(true);
  };

  const canSubmit =
    !!form.company_id &&
    !!form.collateral_type &&
    !!form.status &&
    !createCollateral.isPending;

  return (
    <div>
      <div className="flex items-center gap-3 mb-2 text-sm text-slate-500">
        <Link
          to={`/business/cases/${caseId}`}
          className="inline-flex items-center gap-1 hover:text-business-primary"
        >
          <ArrowRight className="w-4 h-4" />
          חזרה לקייס
        </Link>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-navy mb-1">ביטחונות</h1>
          <p className="text-slate-600">
            מיפוי ביטחונות החברות בקייס ושיוכם להלוואות הקיימות
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          disabled={
            isAdding || !companiesQuery.data || companiesQuery.data.length === 0
          }
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          הוסף ביטחון
        </button>
      </div>

      {(companiesQuery.data?.length ?? 0) === 0 && companiesQuery.isFetched && (
        <div className="card mb-6 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border-amber-200">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            עוד לא הוזנו חברות בקייס. הוסף לפחות חברה אחת לפני הזנת ביטחונות.
          </span>
        </div>
      )}

      {isAdding && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-navy mb-4">ביטחון חדש</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="חברה">
              <select
                value={form.company_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    company_id: e.target.value,
                    secures_facility_id: '',
                  })
                }
                className="input"
              >
                <option value="">— בחר חברה —</option>
                {(companiesQuery.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.short_label}
                    {c.is_primary ? ' ★' : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="סוג ביטחון">
              <select
                value={form.collateral_type}
                onChange={(e) =>
                  setForm({ ...form, collateral_type: e.target.value })
                }
                className="input"
              >
                {COLLATERAL_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="סטטוס">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="input"
              >
                {COLLATERAL_STATUSES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="תיאור" className="md:col-span-2">
              <input
                type="text"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="לדוגמה: דירה ברח׳ הרצל 12, תל אביב"
                className="input"
              />
            </Field>

            <Field label="שווי קיים (₪)">
              <input
                type="number"
                inputMode="numeric"
                value={form.value_current}
                onChange={(e) =>
                  setForm({ ...form, value_current: e.target.value })
                }
                className="input"
              />
            </Field>

            <Field label="Haircut הבנק (%)">
              <input
                type="number"
                step="1"
                value={form.haircut_percent}
                onChange={(e) =>
                  setForm({ ...form, haircut_percent: e.target.value })
                }
                placeholder="70"
                className="input"
              />
            </Field>

            <Field label="משועבד להלוואה" className="md:col-span-2">
              <select
                value={form.secures_facility_id}
                onChange={(e) =>
                  setForm({ ...form, secures_facility_id: e.target.value })
                }
                disabled={!form.company_id}
                className="input disabled:bg-slate-50"
              >
                <option value="">— ללא שיוך —</option>
                {facilitiesForSelectedCompany.map((f) => (
                  <option key={f.id} value={f.id}>
                    {facilityLabel(f)}
                  </option>
                ))}
              </select>
              {form.company_id &&
                facilitiesForSelectedCompany.length === 0 &&
                facilitiesQuery.isFetched && (
                  <p className="mt-1 text-xs text-slate-500">
                    אין הלוואות לחברה זו עדיין — אפשר להזין את הביטחון ללא שיוך
                    ולקשר אותו מאוחר יותר.
                  </p>
                )}
            </Field>

            <Field label="הערות" className="md:col-span-3">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="input"
              />
            </Field>
          </div>

          {createCollateral.isError && (
            <div className="flex items-start gap-2 p-3 mt-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>שגיאה בהוספה: {createCollateral.error.message}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setForm(emptyForm);
              }}
              disabled={createCollateral.isPending}
              className="btn-secondary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => createCollateral.mutate(form)}
              disabled={!canSubmit}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {createCollateral.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              שמור
            </button>
          </div>
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        {collateralsQuery.isLoading ? (
          <div className="p-8 text-center text-slate-500">טוען…</div>
        ) : collateralsQuery.isError ? (
          <div className="p-8 text-center text-red-600 text-sm">
            שגיאה בטעינה: {(collateralsQuery.error as Error).message}
          </div>
        ) : (collateralsQuery.data?.length ?? 0) === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            אין ביטחונות מוזנים. לחץ על "הוסף ביטחון" כדי להתחיל.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <Th>חברה</Th>
                  <Th>סוג</Th>
                  <Th>תיאור</Th>
                  <Th>סטטוס</Th>
                  <Th align="left">שווי (₪)</Th>
                  <Th align="left">Haircut</Th>
                  <Th>משועבד ל</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(collateralsQuery.data ?? []).map((c) => {
                  const company = companyMap.get(c.company_id);
                  const facility = c.secures_facility_id
                    ? facilityMap.get(c.secures_facility_id)
                    : undefined;
                  return (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <Td>{company?.short_label ?? '—'}</Td>
                      <Td>{labelOf(COLLATERAL_TYPES, c.collateral_type)}</Td>
                      <Td>{c.description ?? '—'}</Td>
                      <Td>{labelOf(COLLATERAL_STATUSES, c.status)}</Td>
                      <Td align="left">{fmtMoney(c.value_current)}</Td>
                      <Td align="left">{fmtRate(c.haircut_ratio)}</Td>
                      <Td>
                        {facility ? (
                          facilityLabel(facility)
                        ) : c.secures_facility_id ? (
                          <span className="text-slate-400">
                            {c.secures_facility_id.slice(0, 8)}…
                          </span>
                        ) : (
                          <span className="text-slate-400">ללא שיוך</span>
                        )}
                      </Td>
                      <Td align="left">
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('למחוק את הביטחון?')) {
                              deleteCollateral.mutate(c.id);
                            }
                          }}
                          disabled={deleteCollateral.isPending}
                          className="text-slate-400 hover:text-red-600 transition disabled:opacity-50"
                          aria-label="מחק"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Th({
  children,
  align = 'right',
}: {
  children?: React.ReactNode;
  align?: 'right' | 'left';
}) {
  return (
    <th
      className={`px-4 py-3 font-medium ${
        align === 'left' ? 'text-left' : 'text-right'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'right',
}: {
  children?: React.ReactNode;
  align?: 'right' | 'left';
}) {
  return (
    <td
      className={`px-4 py-3 text-slate-700 ${
        align === 'left' ? 'text-left' : 'text-right'
      }`}
    >
      {children}
    </td>
  );
}
